import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable, Observer, throwError } from 'rxjs';
import { catchError, delayWhen, map, switchMap, take } from 'rxjs/operators';
import { getShelterDataLoaded, getShelterDataSlice } from '../../../../store';
import { LoadShelterData } from '../../../../store/actions/shelter-actions/shelter-actions';
import { ApiService } from './../../../../shared/services/api-service/api-service';

@Injectable({
  providedIn: 'root',
})
export class AttendanceGridService {
  private GRID_TYPE = 'attGrid';

  constructor(private apiService: ApiService, private store: Store<any>) {}

  public getShelterGridConfig$(shelterId: string): Observable<any> {
    const query = `{
      ShelterGridConfig(shelterId: "${shelterId}", gridType: "${this.GRID_TYPE}") {
        checkboxSelection
        field
        filter
        filterParams {
          values
        }
        headerCheckboxSelection
        headerName
        headerTooltip
        lockPinned
        pinned
        resizable
        sortable
        valueFormatter
        valueGetter
        width
      }
    }`;
    const payload = { query, fetchPolicy: 'no-cache' };
    return this.apiService.getStudentsGraphQL(payload).pipe(catchError(err => throwError(err)));
  }

  public getShelterGridData$({ shelterId, columns }): Observable<any> {
    const columnKeys = this.getColumnKeys(columns);
    const query = `{
      ShelterGridData(
        shelterId: "${shelterId}",
        gridType: "${this.GRID_TYPE}",
        columns: [${columnKeys}]
      ) {
        ${columns}
      }
    }`;
    const payload = { query, fetchPolicy: 'no-cache' };
    return this.apiService.getStudentsGraphQL(payload).pipe(catchError(err => throwError(err)));
  }

  private getColumnKeys(projection: string[]): string[] {
    return projection.map(key => {
      return `"${key}"`;
    });
  }

  public getShelterGridDataPayload(columnDefs: any[], shelterId: string): { shelterId: string; columns: string[] } {
    const columns = columnDefs.map(columnDef => {
      return columnDef.field;
    });
    const payload = {
      shelterId,
      columns,
    };
    return payload;
  }

  public getAllShelterData$(shelterId: string) {
    return this.store.select(getShelterDataLoaded).pipe(
      delayWhen(loaded => this.loadShelterData$(loaded, shelterId)),
      switchMap(() => this.getDataFromStore$()),
      take(1),
    );
  }

  private getDataFromStore$(): Observable<any> {
    return this.store.select(getShelterDataSlice).pipe(
      take(1),
      map(({ columnDefs, rowData }) => {
        return { columnDefs, rowData };
      }),
    );
  }

  private loadShelterData$(loaded: boolean, shelterId: string): Observable<any> {
    return Observable.create((observer: Observer<any>) => {
      if (!loaded) {
        this.store.dispatch(
          new LoadShelterData({
            shelterId,
          }),
        );
      } else {
        observer.next('shelter data loaded');
        observer.complete();
      }
    });
  }
}
